import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { supabasePublic } from '../supabaseClient';
import { getBackendURL, basename } from '../env';
import {
  PATIENT_ACCOUNT_TYPE,
  emptyPatientSignupFields,
  validatePatientSignupFields,
  buildPatientSignupFields,
  patientDisplayName,
} from './patientSignupFields';

/**
 * Shown after Google OAuth when the user has no account yet.
 * Collects role and signup reason, then creates the user and redirects to /schedules.
 */
const CompleteSignup = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [accountType, setAccountType] = useState('student');
  const [patientFields, setPatientFields] = useState(emptyPatientSignupFields);
  const [signupReason, setSignupReason] = useState('');
  const lockedReferralName = sessionStorage.getItem('auth_referral_ref') || null;
  const [referralSource, setReferralSource] = useState(lockedReferralName ? 'a friend' : 'Instagram');
  const [referralDetail, setReferralDetail] = useState(lockedReferralName || '');
  const [user, setUser] = useState(null);
  const isPatientSignup = accountType === PATIENT_ACCOUNT_TYPE;

  const referralNeedsDetail = referralSource === 'a friend' || referralSource === 'other';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabasePublic().auth.getSession();
      if (cancelled) return;
      if (!session?.user) {
        navigate((basename || '') + '/auth', { replace: true });
        return;
      }
      setUser(session.user);
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast({ title: 'You must be signed in to continue.', status: 'warning', duration: 4000 });
      return;
    }
    if (!isPatientSignup && !signupReason.trim()) {
      toast({ title: 'Please choose a role and explain how you will use the app.', status: 'warning', duration: 4000 });
      return;
    }
    const patientValidation = isPatientSignup
      ? validatePatientSignupFields(patientFields, user.email)
      : null;
    if (patientValidation) {
      toast({ title: patientValidation, status: 'warning', duration: 4000 });
      return;
    }
    if (!isPatientSignup && referralNeedsDetail && !referralDetail.trim()) {
      toast({ title: 'Please tell us how you heard about the app.', status: 'warning', duration: 4000 });
      return;
    }
    setLoading(true);
    const backendUrl = getBackendURL();
    const signupName = isPatientSignup
      ? patientDisplayName(patientFields)
      : user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0];
    try {
      const res = await fetch(`${backendUrl}/api/complete-google-signup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          email: user.email,
          name: signupName,
          account_type: accountType,
          signup_reason: isPatientSignup
            ? null
            : signupReason.trim() + ' ||| ' + referralSource + (referralNeedsDetail ? ': ' + referralDetail.trim() : ''),
          patient_fields: accountType === PATIENT_ACCOUNT_TYPE
            ? buildPatientSignupFields(patientFields, user.email)
            : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({ title: data.error || 'Failed to complete signup', status: 'error', duration: 5000 });
        setLoading(false);
        return;
      }
      if (accountType === PATIENT_ACCOUNT_TYPE) {
        sessionStorage.removeItem('auth_referral_ref');
        toast({ title: 'Account created. Redirecting...', status: 'success', duration: 2000 });
        const patientRedirectPath = data.patient_match_status === 'verified'
          ? '/schedules'
          : '/auth/patient-verification';
        window.location.href = window.location.origin + (basename || '') + patientRedirectPath;
        return;
      }
      const schemaRes = await fetch(`${backendUrl}/api/assign-user-schema/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, account_type: accountType }),
      });
      if (schemaRes.ok) {
        sessionStorage.setItem('schema_created_' + user.id, 'true');
      }
      sessionStorage.removeItem('auth_referral_ref');
      toast({ title: 'Account created. Redirecting...', status: 'success', duration: 2000 });
      window.location.href = window.location.origin + (basename || '') + '/schedules';
    } catch (err) {
      toast({ title: err.message || 'Network error', status: 'error', duration: 5000 });
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Center minH="40vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!user) return null;

  return (
    <Box maxW="md" mx="auto" mt={8} p={6} borderWidth={1} borderRadius="lg" shadow="md">
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Complete your signup
      </Text>
      <Text color="gray.600" mb={4}>
        {isPatientSignup
          ? 'You signed in with Google. Please confirm your patient portal details to continue.'
          : 'You signed in with Google. Please choose how you will use the app and confirm.'}
      </Text>
      <form onSubmit={handleSubmit}>
        <VStack align="stretch" spacing={4}>
          <FormControl>
            <FormLabel>Email (from Google)</FormLabel>
            <Input value={user.email || ''} isReadOnly variant="filled" size="sm" />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Account type</FormLabel>
            <Box
              as="select"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              w="100%"
              p={2}
              borderWidth={1}
              borderRadius="md"
            >
              <option value="doctor">Doctor / Clinician</option>
              <option value="patient">Patient</option>
              <option value="student">Student</option>
              <option value="medstudent">Medical Student</option>
              <option value="socialworker">Social Worker</option>
              <option value="other">Other</option>
            </Box>
          </FormControl>
          {isPatientSignup && (
            <>
              <FormControl isRequired>
                <FormLabel>First name</FormLabel>
                <Input
                  value={patientFields.patientFirstName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientFirstName: e.target.value }))}
                  placeholder="First name on file"
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Middle name</FormLabel>
                <Input
                  value={patientFields.patientMiddleName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientMiddleName: e.target.value }))}
                  placeholder="Middle name on file (optional)"
                  size="sm"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last name</FormLabel>
                <Input
                  value={patientFields.patientLastName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientLastName: e.target.value }))}
                  placeholder="Last name on file"
                  size="sm"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Date of birth</FormLabel>
                <Input
                  type="date"
                  value={patientFields.patientDob}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientDob: e.target.value }))}
                  size="sm"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Phone number</FormLabel>
                <Input
                  type="tel"
                  value={patientFields.patientPhone}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientPhone: e.target.value }))}
                  placeholder="Phone number on file"
                  size="sm"
                />
              </FormControl>
            </>
          )}
          {!isPatientSignup && (
            <>
              <FormControl isRequired>
                <FormLabel>How will you use the app?</FormLabel>
                <Box
                  as="textarea"
                  value={signupReason}
                  onChange={(e) => setSignupReason(e.target.value)}
                  placeholder="Please explain how you'll use the app (required)"
                  minH="100px"
                  w="100%"
                  p={2}
                  borderWidth={1}
                  borderRadius="md"
                />
              </FormControl>
              {lockedReferralName ? (
                <FormControl>
                  <FormLabel>How did you hear about us?</FormLabel>
                  <Box p={2} borderWidth={1} borderRadius="md" bg="gray.50" color="gray.600" cursor="not-allowed">
                    Referred by: {lockedReferralName}
                  </Box>
                </FormControl>
              ) : (
                <>
                  <FormControl isRequired>
                    <FormLabel>How did you hear about us?</FormLabel>
                    <Box
                      as="select"
                      value={referralSource}
                      onChange={(e) => { setReferralSource(e.target.value); setReferralDetail(''); }}
                      w="100%"
                      p={2}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <option value="Instagram">Instagram</option>
                      <option value="TikTok">TikTok</option>
                      <option value="a friend">A friend</option>
                      <option value="other">Other</option>
                    </Box>
                  </FormControl>
                  {referralNeedsDetail && (
                    <FormControl isRequired>
                      <FormLabel>{referralSource === 'a friend' ? 'Who referred you?' : 'Please specify'}</FormLabel>
                      <Input
                        value={referralDetail}
                        onChange={(e) => setReferralDetail(e.target.value)}
                        placeholder={referralSource === 'a friend' ? "Friend's name or details" : 'How did you hear about us?'}
                        size="sm"
                      />
                    </FormControl>
                  )}
                </>
              )}
            </>
          )}
          <Button type="submit" colorScheme="blue" width="full" isLoading={loading} loadingText="Creating account...">
            Complete signup
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default CompleteSignup;
